import { Injectable, isDevMode } from '@angular/core';
import { loaderTypes } from '@app/shared/component/loader/loader';
import { GraphQLResult } from '@aws-amplify/api';
import { environment } from '@env/environment';
//
import { fetchAuthSession } from '@aws-amplify/auth';
import AWSAppSyncClient, { AUTH_TYPE } from 'aws-appsync';
import gql from 'graphql-tag';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { LoaderService } from '../loader/loader.service';
import * as mutations from './operations/mutations';
import * as resultTypes from './operations/operation-result-types';
import * as queries from './operations/queries';
import * as subscriptions from './operations/subscriptions';

const appsync = new AWSAppSyncClient({
  url: environment.graphqlEndpoint,
  region: environment.region,
  auth: {
    type: AUTH_TYPE.AMAZON_COGNITO_USER_POOLS,

    jwtToken: async () => {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      return idToken;
    },
  },
  disableOffline: true,
});

@Injectable({
  providedIn: 'root',
})
export class GraphQLService {
  reqCount: number = 0;
  resCount: number = 0;

  loaderTypes = loaderTypes;

  constructor(private loaderService: LoaderService, private toastr: ToastrService) {}

  getConnections(params, loader?: loaderTypes, isRelationship?: boolean) {
    this.log(['calling: graphQL-queries.getUserConnections'], 'info');
    this.showLoader(loader);
    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const queryToUse = isRelationship ? queries.getUserConnections : queries.getUserConnections;
      const variables = isRelationship
        ? { from_user_id: params }
        : params?.impersonate_user_id
        ? { impersonate_user_id: params.impersonate_user_id }
        : {};

      const results = appsync.query({
        query: gql(queryToUse),
        variables: variables || {},
        fetchPolicy: 'network-only',
      }) as Promise<GraphQLResult<resultTypes.GetUserConnectionsQuery>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.getUserConnections || res.data });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          this.log(['failed to call: graphQL-queries.getUserConnections', error], 'error');
          this.resCount++;
          this.onEnd();
          subscriber.error({ status: 400, data: null, error: error });
        });
    });
  }

  /**
   * addConnection @params {
   * to_user_id: Int,
   * relationship_id: Int }
   */
  addConnection(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-mutation.addConnection'], 'info');
    this.showLoader(loader);

    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.mutate({
        mutation: gql(mutations.addConnection),
        variables: params,
      }) as Promise<GraphQLResult<resultTypes.AddConnectionMutation>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.addConnection });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          this.log(['failed to call: graphQL-mutation.addConnection', error], 'error');
          this.resCount++;
          this.onEnd();
          subscriber.error({ status: 400, data: null, error: error });
        });
    });
  }

  /**
   * dropConnection @params {
   * from_user_id: Int,
   * to_user_id: Int}
   */
  dropConnection(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-mutation.dropConnection'], 'info');
    this.showLoader(loader);

    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.mutate({
        mutation: gql(mutations.dropConnection),
        variables: params,
      }) as Promise<GraphQLResult<resultTypes.DropConnectionMutation>>;
      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.dropConnection });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          this.log(['failed to call: graphQL-mutation.dropConnection', error], 'error');
          this.resCount++;
          this.onEnd();
          subscriber.error({ status: 400, data: null, error: error });
        });
    });
  }

  /**
   * updateConnection @params {
   * from_user_id: Int!,
   * to_user_id: Int!,
   * status: string invite | pending | approved (optional),
   * relationship_id: Int (optional)}
   */
  updateConnection(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-mutation.updateConnection'], 'info');
    this.showLoader(loader);

    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.mutate({
        mutation: gql(mutations.updateConnection),
        variables: params,
      }) as Promise<GraphQLResult<resultTypes.UpdateConnectionMutation>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.updateConnection });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          this.log(['failed to call: graphQL-mutation.updateConnection', error], 'error');
          this.resCount++;
          this.onEnd();
          subscriber.error({ status: 400, data: null, error: error });
        });
    });
  }

  /**
   * search @params {
   * search_val: String,
   * search_type: String}
   */
  search(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-query.search'], 'info');
    this.showLoader(loader);

    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.query({
        query: gql(queries.search),
        variables: params,
        fetchPolicy: 'network-only',
      }) as Promise<GraphQLResult<resultTypes.SearchQuery>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.search });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          this.log(['failed to call: graphQL-queries.search', error], 'error');
          this.resCount++;
          this.onEnd();
          subscriber.error({ status: 400, data: null, error: error });
        });
    });
  }

  sendMessage(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-mutation.sendMessage'], 'info');
    this.showLoader(loader);

    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.mutate({
        mutation: gql(mutations.sendMessage),
        variables: params,
      }) as Promise<GraphQLResult<resultTypes.SendMessageMutation>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.sendMessage });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          this.log(['failed to call: graphQL-mutation.sendMessage', error], 'error');
          this.resCount++;
          this.onEnd();
          subscriber.error({ status: 400, data: null, error: error });
        });
    });
  }

  getMessages(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-queries.getMessages'], 'info');
    this.showLoader(loader);
    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.query({
        query: gql(queries.getMessages),
        variables: params,
        fetchPolicy: 'network-only',
      }) as Promise<GraphQLResult<resultTypes.GetMessagesQuery>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.getMessages });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          subscriber.error(error);
          this.resCount++;
          this.onEnd();
        });
    });
  }

  getTemplateMessages(params, loader?: loaderTypes) {
    this.log(['calling: graphQL-queries.getTemplateMessages'], 'info');
    this.showLoader(loader);
    this.reqCount++;

    return new Observable<any>((subscriber) => {
      const results = appsync.query({
        query: gql(queries.getTemplateMessages),
        variables: params,
        fetchPolicy: 'network-only',
      }) as Promise<GraphQLResult<resultTypes.GetTemplateMessagesQuery>>;

      results
        .then((res) => {
          subscriber.next({ status: 200, data: res.data.getTemplateMessages });
          this.resCount++;
          this.onEnd();
        })
        .catch((error) => {
          subscriber.error(error);
          this.resCount++;
          this.onEnd();
        });
    });
  }

  onNewMessage(pk: string): Observable<any> {
    this.log(['calling: graphQL-subscriptions.onNewMessage'], 'info');

    return new Observable<any>((subscriber) => {
      const subscription = appsync
        .subscribe({
          query: gql(subscriptions.onNewMessage),
          variables: { pk },
        })
        .subscribe({
          next: (result) => {
            subscriber.next({ status: 200, data: result.data.onNewMessage });
          },
          error: (error) => {
            subscriber.error(error);
          },
        });

      // Return cleanup function
      return () => {
        subscription.unsubscribe();
      };
    });
  }

  private log(logMessage, type) {
    if (type === 'error') {
      if (logMessage[2] != undefined && logMessage[2].message === 'Network Error') {
        //this.toastr.error('Your access to the Internet seems to be disconnected. Please try again later');
      } else {
        this.toastr.error('Something seems to be wrong. Please try again later. If the problem persists please contact admin@kinnect.us');
      }
    }

    if (isDevMode()) {
      switch (type) {
        case 'info':
          break;
        case 'error':
          console.error(logMessage.join(' '));
      }
    }
  }

  private onEnd(): void {
    if (this.reqCount == this.resCount) {
      this.hideLoader();
    }
  }

  private showLoader(loaderType?: loaderTypes): void {
    this.loaderService.show(loaderType);
  }

  private hideLoader(): void {
    this.loaderService.hide();
  }
}
